import React, { useState, useEffect } from "react";
import BitrixAPI from "../api/b24";
import FilePreview from "../components/FileViewer";
import LoadingButton from "../components/LoadingButton";
import FileUpload from "../components/FileUpload";
import { useHeader } from "../contexts/HeaderContext";

const PickupOrdersPageB24 = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingOrders, setProcessingOrders] = useState(new Set());
  const [error, setError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [uploadedPhotos, setUploadedPhotos] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedOrders, setExpandedOrders] = useState(new Set());
  const { setHeaderExtras } = useHeader();
  
  // Компонент свайп-кнопки
  const SwipeToConfirmButton = ({ orderId, onConfirm, isLoading, hasPhoto = false }) => {
    const [dragX, setDragX] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [isCompleted, setIsCompleted] = useState(false);
    const [startX, setStartX] = useState(0);
    const maxDrag = 200; // Максимальное расстояние для свайпа
    const threshold = 150; // Пороговое значение для активации
    const sliderRef = React.useRef(null);
    
    const handleSliderMouseDown = (e) => {
      setIsDragging(true);
      setStartX(e.clientX);
      e.preventDefault();
      e.stopPropagation();
    };
    
    const handleSliderTouchStart = (e) => {
      setIsDragging(true);
      setStartX(e.touches[0].clientX);
      e.preventDefault();
      e.stopPropagation();
    };
    
    const handleGlobalMouseMove = (e) => {
      if (!isDragging) return;
      const deltaX = e.clientX - startX;
      setDragX(Math.max(0, Math.min(deltaX, maxDrag)));
      e.preventDefault();
    };
    
    const handleGlobalTouchMove = (e) => {
      if (!isDragging) return;
      const deltaX = e.touches[0].clientX - startX;
      setDragX(Math.max(0, Math.min(deltaX, maxDrag)));
      e.preventDefault();
    };
    
    const handleEnd = () => {
      if (dragX >= threshold && !isCompleted) {
        setIsCompleted(true);
        onConfirm(orderId);
      }
      setIsDragging(false);
      setDragX(0);
      setStartX(0);
    };
    
    // Добавляем глобальные обработчики только при перетаскивании
    React.useEffect(() => {
      if (isDragging) {
        document.addEventListener('mousemove', handleGlobalMouseMove, { passive: false });
        document.addEventListener('mouseup', handleEnd);
        document.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
        document.addEventListener('touchend', handleEnd);
        
        return () => {
          document.removeEventListener('mousemove', handleGlobalMouseMove);
          document.removeEventListener('mouseup', handleEnd);
          document.removeEventListener('touchmove', handleGlobalTouchMove);
          document.removeEventListener('touchend', handleEnd);
        };
      }
    }, [isDragging, startX, dragX, threshold, isCompleted]);
    
    const progress = (dragX / maxDrag) * 100;
    const isActivated = dragX >= threshold;
    
    return (
      <div className="relative w-full h-16 sm:h-12 bg-gray-200 rounded-lg overflow-hidden select-none pointer-events-none">
        {/* Прогресс-бар */}
        <div 
          className={`absolute inset-y-0 left-0 transition-colors duration-200 pointer-events-none ${
            isActivated ? 'bg-green-400' : hasPhoto ? 'bg-orange-400' : 'bg-gray-400'
          }`}
          style={{ width: `${progress}%` }}
        />
        
        {/* Слайдер - только здесь можно начать перетаскивание */}
        <div 
          ref={sliderRef}
          className={`absolute top-1 left-1 w-14 h-14 sm:w-10 sm:h-10 rounded-md shadow-md transition-colors duration-200 flex items-center justify-center cursor-grab pointer-events-auto ${
            isActivated ? 'bg-green-600' : hasPhoto ? 'bg-orange-600' : 'bg-gray-600'
          } ${isDragging ? 'cursor-grabbing' : ''}`}
          style={{ 
            transform: `translateX(${dragX}px)`,
            transition: isDragging ? 'none' : 'transform 0.3s ease'
          }}
          onMouseDown={handleSliderMouseDown}
          onTouchStart={handleSliderTouchStart}
        >
          {isLoading ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <span className="text-white text-xs">→</span>
          )}
        </div>
        
        {/* Текст */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span className={`text-sm font-medium transition-colors duration-200 ${
            isActivated ? 'text-white' : 'text-gray-600'
          }`}>
            {isLoading ? 'Обработка...' : isActivated ? 'Отпустите для выдачи' : hasPhoto ? 'Свайп для выдачи →' : 'Загрузите фото для выдачи'}
          </span>
        </div>
      </div>
    );
  };

  const handleStageUpdate = async (orderId) => {
    try {
      setProcessingOrders(prev => new Set([...prev, orderId]));
      setError(null);
      
      const result = await BitrixAPI.updateStagepickup(orderId);
      
      if (result && result.success) {
        setOrders(prevOrders => prevOrders.filter(order => order.id !== orderId));
        console.log(`Заказ ${orderId} успешно выдан клиенту`);
      } else {
        throw new Error(result?.message || "Не удалось обновить стадию");
      }
    } catch (error) {
      console.error("Ошибка при обновлении стадии:", error);
      
      // Получаем сообщение от сервера если есть
      const errorMessage = error.response?.data?.message || error.message;
      
      setError(`Ошибка при обновлении заказа ${orderId}: ${errorMessage}`);
      alert(`Ошибка: ${errorMessage}`);
    } finally {
      setProcessingOrders(prev => {
        const newSet = new Set(prev);
        newSet.delete(orderId);
        return newSet;
      });
    }
  };

  const handleUploadSuccess = (message, orderId) => {
    setUploadSuccess(message);
    setUploadError(null);
    // Отмечаем что для этого заказа загружено фото
    setUploadedPhotos(prev => new Set([...prev, orderId]));
    // Автоматически скрыть уведомление через 5 секунд
    setTimeout(() => setUploadSuccess(null), 5000);
  };

  const handleUploadError = (message) => {
    setUploadError(message);
    setUploadSuccess(null);
  };

  const handleToggleOrder = (orderId) => {
    setExpandedOrders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  // Фильтрация заказов по поисковому запросу
  const filteredOrders = orders.filter(order =>
    order.orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.id.toString().includes(searchTerm)
  );

  // Вставка поиска и счётчика в хедер
  useEffect(() => {
    setHeaderExtras(
      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <input
          type="text"
          placeholder="Поиск по номеру, названию..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          style={{ width: "220px" }}
        />
        <span className="text-sm text-gray-600">
          Заказов: <strong>{orders.length}</strong>
        </span>
      </div>
    );
    return () => setHeaderExtras(null);
  }, [searchTerm, orders.length]);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await BitrixAPI.getActiveOrderspickup();
        setOrders(response || []);
      } catch (err) {
        console.error("Ошибка загрузки заказов:", err);
        setError(`Ошибка загрузки заказов: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  if (loading) return <div className="p-4">Ойланып жатырмын ...</div>;

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold mb-6">Заказы на самовывоз (B24)</h1>
      
      
      {/* Отображение ошибок */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          <strong>Ошибка:</strong> {error}
          <button 
            onClick={() => setError(null)}
            className="ml-2 text-red-500 hover:text-red-700"
          >
            ✕
          </button>
        </div>
      )}

      {/* Уведомления о загрузке файлов */}
      {uploadSuccess && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-4">
          <strong>Успех:</strong> {uploadSuccess}
          <button 
            onClick={() => setUploadSuccess(null)}
            className="ml-2 text-green-500 hover:text-green-700"
          >
            ✕
          </button>
        </div>
      )}

      {uploadError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          <strong>Ошибка загрузки:</strong> {uploadError}
          <button 
            onClick={() => setUploadError(null)}
            className="ml-2 text-red-500 hover:text-red-700"
          >
            ✕
          </button>
        </div>
      )}
      
      <div className="space-y-4">
        {filteredOrders && filteredOrders.length > 0 ? (
          filteredOrders.map((order) => (
            <div 
              key={order.id} 
              className="bg-white p-4 shadow rounded-lg transform transition-all duration-300 hover:shadow-lg"
            >
              <div 
                className="flex justify-between items-center mb-4 cursor-pointer"
                onClick={() => handleToggleOrder(order.id)}
              >
                <div className="flex-1">
                  <h2 className="text-lg font-medium">Заказ №{order.orderNumber}</h2>
                  <p className="text-gray-600">{order.title}</p>
                  <p className="text-xs text-gray-500">ID: {order.id}</p>
                </div>
                <div className="text-right flex items-center gap-2">
                  <span className="text-sm text-orange-600 font-medium">Готов к выдаче</span>
                  <span className="text-gray-400 text-lg">
                    {expandedOrders.has(order.id) ? '▲' : '▼'}
                  </span>
                </div>
              </div>

              {/* Файлы чертежей - показывать только при раскрытии заказа */}
              {expandedOrders.has(order.id) && (
                <>
                  {order.productionFiles && order.productionFiles.length > 0 && (
                    <div className="mt-4">
                      <h3 className="text-sm text-gray-800 mb-2">Чертежи для производства:</h3>
                      {order.productionFiles.map((file) => (
                        <FilePreview 
                          key={file.id} 
                          file={file} 
                          dealId={order.id}
                        />
                      ))}
                    </div>
                  )}

                  {order.files && order.files.length > 0 && (
                    <div className="mt-4">
                      <h3 className="text-sm text-gray-800 mb-2">Деталировка к чертежу:</h3>
                      {order.files.map((file) => (
                        <FilePreview 
                          key={file.id} 
                          file={file} 
                          dealId={order.id}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* Компонент загрузки фото для выдачи */}
              <div className="mt-4">
                <h3 className="text-sm text-gray-800 mb-2">Загрузите фото для выдачи заказа:</h3>
                <FileUpload 
                  orderId={order.id}
                  onUploadSuccess={(message) => handleUploadSuccess(message, order.id)}
                  onUploadError={handleUploadError}
                  uploadEndpoint="upload-pickup-photo"
                  fieldType="pickup"
                />
              </div>

              {/* Свайп-кнопка во всю ширину блока */}
              <div className="mt-4">
                <SwipeToConfirmButton
                  orderId={order.id}
                  onConfirm={handleStageUpdate}
                  isLoading={processingOrders.has(order.id)}
                  hasPhoto={uploadedPhotos.has(order.id)}
                />
              </div>
            </div>
          ))
        ) : (
          <div className="bg-gray-50 p-8 rounded-lg text-center">
            <p className="text-gray-600">
              {searchTerm ? 'Заказы по запросу не найдены' : 'Нет заказов для самовывоза'}
            </p>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="mt-2 text-blue-600 hover:text-blue-800 underline"
              >
                Сбросить поиск
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PickupOrdersPageB24;